<!--
  ~ Copyright (C) 2026 - Potentially Ltd
  ~
  ~ Please see distribution for license.
  -->

@if (organizationData$ | async; as organizationData) {
  <ptl-v3-auth-layout [organizationData]="organizationData" [showMaintenanceOverlay]="showMaintenanceOverlay">
    @if (!isForgotForm) {
      <div class="v3-headline">
        <h1 class="v3-title">{{ 'translations.public.alumni.title.login' | transloco }}</h1>
        @if (isBcuLogin) {
          <p
            class="v3-description fs-body-m"
            (click)="onDescriptionClick($event)"
            (keydown.enter)="onDescriptionClick($event)"
            [innerHTML]="'translations.public.alumni.bcuDescription' | transloco: { organizationName: organizationData.name } | safeHtml"
          ></p>
        } @else if (isLboroLogin) {
          <p
            class="v3-description fs-body-m"
            (click)="onDescriptionClick($event)"
            (keydown.enter)="onDescriptionClick($event)"
            [innerHTML]="'translations.public.alumni.lboroDescription' | transloco: { organizationName: organizationData.name } | safeHtml"
          ></p>
        } @else if (isCardiffLogin) {
          <p
            class="v3-description fs-body-m"
            (click)="onDescriptionClick($event)"
            (keydown.enter)="onDescriptionClick($event)"
            [innerHTML]="
              'translations.public.alumni.cardiffDescription' | transloco: { organizationName: organizationData.name } | safeHtml
            "
          ></p>
        } @else if (isCoventryLogin) {
          <p
            class="v3-description fs-body-m"
            (click)="onDescriptionClick($event)"
            (keydown.enter)="onDescriptionClick($event)"
            [innerHTML]="
              'translations.public.alumni.coventryDescription' | transloco: { organizationName: organizationData.name } | safeHtml
            "
          ></p>
        } @else {
          <p
            class="v3-description fs-body-m"
            (click)="onDescriptionClick($event)"
            (keydown.enter)="onDescriptionClick($event)"
            [innerHTML]="
              'translations.public.alumni.description'
                | transloco: { organizationName: organizationData.name, year: year, loginUrl: loginUrl }
                | safeHtml
            "
          ></p>
        }
      </div>

      <div
        class="v3-login-form"
        [formGroup]="loginFrom"
        role="form"
        [attr.aria-label]="'translations.global.ariaLabels.signInForm' | transloco"
      >
        @let isLoggingIn = loginInProgress$ | async;

        <ptl-sg-input
          class="v3-field"
          type="email"
          formControlName="email"
          [label]="'translations.global.input.label.email' | transloco"
          (keydown.enter)="onLoginUser()"
        ></ptl-sg-input>
        @if (loginFrom.controls.email.touched && loginFrom.controls.email.hasError('required')) {
          <p class="v3-field-error fs-body-xs-alt">{{ 'translations.global.input.error.enterEmail' | transloco }}</p>
        }

        <div class="v3-password-field">
          <ptl-sg-input
            class="v3-field"
            [type]="showPasswordSelected ? 'text' : 'password'"
            formControlName="password"
            [label]="'translations.global.input.label.password' | transloco"
            (keydown.enter)="onLoginUser()"
          ></ptl-sg-input>
          <button
            type="button"
            class="v3-password-toggle"
            [attr.aria-label]="'translations.global.ariaLabels.showOrHidePassword' | transloco"
            (click)="showPasswordSelected = !showPasswordSelected"
          >
            <ptl-icon [icon]="showPasswordSelected ? LucideEyeOff : LucideEye" size="xs"></ptl-icon>
          </button>
        </div>
        @if (loginFrom.controls.password.touched && loginFrom.controls.password.hasError('required')) {
          <p class="v3-field-error fs-body-xs-alt">{{ 'translations.global.input.error.enterPassword' | transloco }}</p>
        }

        <div class="v3-actions">
          <ptl-sg-button type="flat" color="primary" class="v3-submit" [disabled]="isLoggingIn" (clicked)="onLoginUser()">
            @if (!isLoggingIn) {
              <span>{{ 'translations.public.button.login' | transloco }}</span>
            } @else {
              <span class="v3-submit-loading">
                <span>{{ 'translations.public.button.loggingIn' | transloco }}</span>
                <mat-spinner [diameter]="18"></mat-spinner>
              </span>
            }
          </ptl-sg-button>

          <div class="v3-actions-bottom">
            <button type="button" class="v3-link-action" (click)="onForgotPassword()">
              {{ 'translations.public.alumni.button.resetPasswordFirstLogin' | transloco }}
            </button>
          </div>
        </div>
      </div>
    } @else {
      <re-captcha #captchaElem [siteKey]="reCaptchaSiteKeyV2" (resolved)="proceedIfValid($event)" size="invisible"></re-captcha>

      <div class="v3-headline">
        <h1 class="v3-title">{{ 'translations.public.alumni.title.forgotPassword' | transloco }}</h1>
      </div>

      <div
        class="v3-login-form"
        [formGroup]="forgotPasswordForm"
        role="form"
        [attr.aria-label]="'translations.global.ariaLabels.forgotPasswordForm' | transloco"
      >
        <ptl-sg-input
          class="v3-field"
          type="email"
          formControlName="email"
          [label]="'translations.global.input.label.email' | transloco"
          (keydown.enter)="sendRecoveryRequest()"
        ></ptl-sg-input>
        @if (forgotPasswordForm.controls.email.touched && forgotPasswordForm.controls.email.hasError('required')) {
          <p class="v3-field-error fs-body-xs-alt">{{ 'translations.global.input.error.enterEmail' | transloco }}</p>
        }

        <div class="v3-actions">
          <ptl-sg-button type="flat" color="primary" class="v3-submit" [disabled]="forgotIsProgress" (clicked)="sendRecoveryRequest()">
            @if (!forgotIsProgress) {
              <span>{{ 'translations.public.button.resetPassword' | transloco }}</span>
            } @else {
              <span class="v3-submit-loading">
                <span>{{ 'translations.public.button.resettingPassword' | transloco }}</span>
                <mat-spinner [diameter]="18"></mat-spinner>
              </span>
            }
          </ptl-sg-button>

          <div class="v3-actions-bottom">
            <button type="button" class="v3-link-action" (click)="onBackClicked()">
              {{ 'translations.public.alumni.button.backToLogin' | transloco }}
            </button>
          </div>
        </div>
      </div>
    }
  </ptl-v3-auth-layout>
} @else {
  <ptl-v3-auth-loading></ptl-v3-auth-loading>
}
